import CreateButton from "../components/CreateButton"
import { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import api from '../api';
import MainContext from '../common/MainContext';
import { canAccessPage } from '../common/permissions';
import Button from '../components/Button';
import MeatBallsMenu from '../components/MeatBallsMenu';
import { ReactComponent as CopyIcon } from '../assets/images/icons/ic-copy.svg';
import { ReactComponent as PauseIcon } from '../assets/images/icons/ic-pause.svg';
import { ReactComponent as DeleteIcon } from '../assets/images/icons/ic-delete.svg';
import Card from '../components/cards/Card';
import Dialog from '../components/dialogs/Dialog';
import EmptyContent from '../components/EmptyContent';
import StatusBadge from '../components/StatusBadge';
import Table from '../components/Table';
import TextInput from '../components/TextInput';
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout';
import { quizApiError } from '../utils/courseQuizzes';
import styles from './CourseQuizCampaigns.module.css';

export default function CourseQuizCampaigns() {
    const navigate = useNavigate();
    const { user } = useContext(MainContext);
    const editable = canAccessPage(user, 'course-quiz-campaigns', 'edit');
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [deleting, setDeleting] = useState(null);
    const load = useCallback(async () => {
        setLoading(true); setError('');
        try { setCampaigns(await api.get('/admin/course-quiz-campaigns')); }
        catch (e) { setError(quizApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);
    const action = async (campaign, type) => {
        if (!editable || busy !== null) return;
        setBusy(campaign.id); setError('');
        try {
            if (type === 'delete') {
                await api.delete(`/admin/course-quiz-campaigns/${campaign.id}`);
                setDeleting(null);
            } else {
                const result = await api.post(`/admin/course-quiz-campaigns/${campaign.id}/${type}`);
                if (type === 'duplicate') { navigate(`/campaigns/${result.id}`); return; }
            }
            await load();
        } catch (e) { setError(quizApiError(e)); }
        finally { setBusy(null); }
    };
    const visible = campaigns.filter(c => `${c.title} ${c.slug}`.toLowerCase().includes(search.trim().toLowerCase()) && (filter === 'all' || (filter === 'active' ? c.active : !c.active)));
    const rows = visible.map(c => ({
        id: c.id,
        title: { value: c.title, type: 'string', component: <div className={styles.campaignName}><Link to={`/campaigns/${c.id}`} onClick={event => event.stopPropagation()}>{c.title}</Link><span>/campaigns/{c.slug}</span></div> },
        type: c.configuration?.type === 'download' ? 'Download' : 'Assessment',
        content: c.configuration?.type === 'download'
            ? `${c.configuration?.files?.length || 0} materiali`
            : `${c.configuration?.questions?.length || 0} domande · ${c.configuration?.rewards?.length || 0} fasce premio`,
        status: { value: c.active ? 'Attiva' : 'Bozza / inattiva', type: 'string', component: <StatusBadge appearance="soft" label={c.active ? 'Attiva' : 'Bozza / inattiva'} color={c.active ? 'var(--sf-green)' : 'var(--sf-grey)'} /> },
        actions: editable ? <MeatBallsMenu disabled={busy !== null} style={{ padding: '.5rem' }} actions={[
            { label: 'Duplica', icon: CopyIcon, onClick: () => action(c, 'duplicate') },
            ...(c.active
                ? [{ label: 'Disattiva', icon: PauseIcon, onClick: () => action(c, 'pause') }]
                : [{ label: 'Elimina', icon: DeleteIcon, style: { color: 'var(--sf-red)' }, onClick: () => { setError(''); setDeleting(c); } }]),
        ]} /> : null,
    }));
    return <HeaderFooterLayout><HelmetProvider><Helmet><title>Campagne</title></Helmet></HelmetProvider>
        <div className={styles.pageShell}><main className={styles.page}>
            <header className={styles.header}><div><p className={styles.eyebrow}>Acquisizione e formazione</p><h1>Campagne</h1><p>Gestisci assessment, lezioni gratuite e materiali da scaricare.</p></div>
                {editable && <CreateButton onClick={() => navigate('/campaigns/new')}>Crea campagna</CreateButton>}
            </header>
            {error && !deleting && <div className={styles.error} role="alert">{error} <button onClick={load}>Riprova</button></div>}
            <Card appearance="modern" integratedTable title="Elenco campagne" actions={<span className={styles.resultCount} role="status">{loading ? 'Caricamento…' : `${visible.length} campagne`}</span>}>
                <div className={styles.listToolbar}>
                    <label className={styles.listSearch}><span className={styles.srOnly}>Cerca campagna</span><TextInput appearance="modern" type="search" placeholder="Cerca per nome o slug…" value={search} onKeyUp={setSearch} /></label>
                    <div className={styles.listFilters} role="group" aria-label="Filtra campagne">{[{ id: 'all', name: 'Tutte' }, { id: 'active', name: 'Attive' }, { id: 'inactive', name: 'Bozze e inattive' }].map(item => <button key={item.id} type="button" aria-pressed={filter === item.id} onClick={() => setFilter(item.id)}>{item.name}</button>)}</div>
                </div>
                <div className={styles.campaignTable}><Table appearance="minimal" loading={loading} loadingN={6}
                    onRowClick={row => navigate(`/campaigns/${row.id}`)}
                    style={{ border: 0, borderRadius: 0, boxShadow: 'none', marginTop: 0 }} defaultSort={{ key: 'id', direction: 'desc' }}
                    columns={[
                        { key: 'title', label: 'Campagna', type: 'custom', wrap: true },
                        { key: 'type', label: 'Tipo', type: 'string' },
                        { key: 'content', label: 'Contenuti', type: 'string' },
                        { key: 'status', label: 'Stato', type: 'custom' },
                        ...(editable ? [{ key: 'actions', label: 'Azioni', type: 'custom', handleClick: true }] : []),
                    ]} elements={rows} /></div>
                {!loading && !visible.length && <EmptyContent title={search || filter !== 'all' ? 'Nessuna campagna trovata' : 'Nessuna campagna creata'} message={search || filter !== 'all' ? 'Prova a cambiare ricerca o filtro.' : 'Crea una campagna per offrire assessment o materiali gratuiti.'} />}
            </Card>
        </main></div>
        <Dialog appearance="modern" style={{ width: 'min(560px, 100%)' }} open={Boolean(deleting)} title="Elimina campagna"
            onClose={() => { if (busy === null) setDeleting(null); }}
            action={<div className={styles.actions}><Button appearance="subtle" disabled={busy !== null} onClick={() => setDeleting(null)}>Annulla</Button><Button additionalClass={styles.primaryAction} accentColor="var(--sf-red)" disabled={busy !== null} onClick={() => action(deleting, 'delete')}>{busy !== null ? 'Eliminazione…' : 'Elimina'}</Button></div>}>
            <div className={styles.confirmContent}><p>Eliminare “{deleting?.title}”? È possibile solo se non ci sono partecipazioni. L'operazione non può essere annullata.</p>{error && <p className={styles.error} role="alert">{error}</p>}</div>
        </Dialog>
    </HeaderFooterLayout>;
}
